import { useState, useRef, useEffect, useMemo, MouseEvent, CSSProperties } from "react";
import ReactDOM from "react-dom";
import { TestCaseStep } from "./TestCaseTypes";
import {
  SubsysIcon,
  CloseFadedIcon,
  AddTelecommandIcon,
  EmptyPageIcon,
  ThreeDotsIcon,
  DragHandleIcon,
  HexIcon,
  PlusIcon,
  CopyIcon,
  PasteIcon,
  CheckboxCheckedIcon,
  CheckboxUncheckedIcon,
  CheckboxIndeterminateIcon,
  CriticalIcon,
} from "./svgs/TestCaseSvgs";
import StepDetailOverlay from "./StepDetailOverlay";
import CustomToolTip from "../../../CommonComponents/CustomToolTip";
import InlineInput from "../../../CommonComponents/InlineInput/InlineInput";

interface StepsDialogProps {
  testCaseName: string | undefined;
  testCaseSteps: TestCaseStep[];
  selectedCommand: { label: string; value: unknown } | null;
  component: { label: string; value: unknown } | null;
  delay: string;
  timeout: string;
  isLoading?: boolean;
  busComponents?: { label: string; value: unknown }[];
  blinkStepIdx?: number | number[] | null;
  onTestCaseNameChange: (name: string) => void;
  onClose: () => void;
  onEditStep: (idx: number) => void;
  onAddTelecommand: (insertIdx?: number) => void;
  onCloneStep: (idx: number) => void;
  onReorderStep: (fromIdx: number, toIdx: number) => void;
  onDeleteStep: (idx: number) => void;
  showStepsDialog: boolean;
  onPasteSteps?: (steps: TestCaseStep[]) => void;
}

const STEPS_SHIMMER_CYCLE_MS = 1400;
const STEPS_ROWS_SETTLE_MS = 450;
const formatMs = (value: string | undefined): string => (
  value === undefined || value === "" ? "—" : `${value} ms`
);

const StepsDialog = ({
  testCaseName,
  testCaseSteps,
  selectedCommand,
  component,
  delay,
  timeout,
  isLoading = false,
  busComponents = [],
  blinkStepIdx = null,
  onTestCaseNameChange,
  onClose,
  onEditStep,
  onAddTelecommand,
  onCloneStep,
  onReorderStep,
  onDeleteStep,
  showStepsDialog,
  onPasteSteps,
}: StepsDialogProps): JSX.Element => {
  const [selectedDetailIdx, setSelectedDetailIdx] = useState<number | null>(null);
  const [showStepsShimmer, setShowStepsShimmer] = useState(isLoading);
  const [insertZonesArmed, setInsertZonesArmed] = useState(!isLoading);
  const [insertZonesReady, setInsertZonesReady] = useState(!isLoading);
  const [isEditingName, setIsEditingName] = useState(false);
  const [recalcTrigger, setRecalcTrigger] = useState(false);
  const shimmerStartedAtRef = useRef<number>(isLoading ? Date.now() : 0);

  // Three-dot menu state
  const [openMenuIdx, setOpenMenuIdx] = useState<number | null>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const menuBtnRefs = useRef<Map<number, HTMLButtonElement>>(new Map());
  const portalMenuRef = useRef<HTMLDivElement | null>(null);

  // Row refs for scroll-into-view
  const rowRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  // Drag state
  const [draggingIdx, setDraggingIdx] = useState<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);
  const stepRowsSignature = testCaseSteps
    .map((step, idx) => `${idx}:${step.teleData?.label ?? ""}:${step.delay}:${step.timeout}`)
    .join("|");

  // ── Multi-select + Copy/Paste state ────────────────────────────
  // Stable per-step IDs via WeakMap keyed by step object reference.
  // Reorder/insert/delete preserve refs of unmoved/moved items, so selection survives them.
  // Edit replaces the step with a new object → selection auto-drops it (intended).
  const stepIdMapRef = useRef<WeakMap<TestCaseStep, string>>(new WeakMap());
  const stepIdCounterRef = useRef(0);
  const getStepId = (step: TestCaseStep): string => {
    let id = stepIdMapRef.current.get(step);
    if (!id) {
      id = `step-${++stepIdCounterRef.current}`;
      stepIdMapRef.current.set(step, id);
    }
    return id;
  };

  // Pre-compute id-per-row for stable referencing in render and effects.
  const stepIds = useMemo(() => testCaseSteps.map(getStepId), [testCaseSteps]);
  const idToIdx = useMemo(() => {
    const m = new Map<string, number>();
    stepIds.forEach((id, i) => m.set(id, i));
    return m;
  }, [stepIds]);

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // Clipboard tracks items by stable ID + frozen snapshot. Filtering on uncheck
  // removes the unchecked entry while preserving the snapshot for the rest.
  const [clipboard, setClipboard] = useState<{ id: string; step: TestCaseStep }[] | null>(null);

  // Drop IDs from selection that no longer exist (delete / edit-replace).
  useEffect(() => {
    setSelectedIds(prev => {
      const next = new Set<string>();
      prev.forEach(id => { if (idToIdx.has(id)) next.add(id); });
      return next.size === prev.size ? prev : next;
    });
  }, [idToIdx]);

  // Keep clipboard in sync with selection — items the user unchecks
  // are removed from the copied set. Saari uncheck ho jaaye to clipboard clear.
  useEffect(() => {
    setClipboard(prev => {
      if (!prev) return prev;
      const filtered = prev.filter(entry => selectedIds.has(entry.id));
      if (filtered.length === 0) return null;
      if (filtered.length === prev.length) return prev;
      return filtered;
    });
  }, [selectedIds]);

  const totalSelectableCount = testCaseSteps.length;
  const isAllSelected = totalSelectableCount > 0 && selectedIds.size === totalSelectableCount;
  const isPartiallySelected = selectedIds.size > 0 && !isAllSelected;

  const toggleRowSelection = (id: string, e?: React.MouseEvent): void => {
    e?.stopPropagation();
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = (): void => {
    setSelectedIds(prev => {
      if (prev.size === totalSelectableCount) return new Set();
      return new Set(stepIds);
    });
  };

  const clearSelection = (): void => {
    setSelectedIds(new Set());
    setClipboard(null);
  };

  const handleCopy = (): void => {
    if (selectedIds.size === 0) return;
    const ordered = stepIds.filter(id => selectedIds.has(id));
    const snapshot = ordered
      .map(id => {
        const step = testCaseSteps[idToIdx.get(id)!];
        return step ? { id, step: JSON.parse(JSON.stringify(step)) as TestCaseStep } : null;
      })
      .filter((entry): entry is { id: string; step: TestCaseStep } => entry !== null);
    setClipboard(snapshot);
  };

  const handlePaste = (): void => {
    if (!clipboard || clipboard.length === 0 || !onPasteSteps) return;
    onPasteSteps(clipboard.map(entry => entry.step));
    setClipboard(null);
    setSelectedIds(new Set());
  };

  // Esc key clears selection (only while dialog is mounted).
  useEffect(() => {
    if (selectedIds.size === 0 && !clipboard) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        clearSelection();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [selectedIds.size, clipboard]);

  const isBlinking = (idx: number): boolean => {
    if (blinkStepIdx == null) return false;
    return Array.isArray(blinkStepIdx) ? blinkStepIdx.includes(idx) : blinkStepIdx === idx;
  };

  // After paste: scroll the first newly-added row into view so the user can see what got added.
  // Triggers when blinkStepIdx becomes an array (paste signal); single-number blink (edit/clone) is unchanged.
  useEffect(() => {
    if (!Array.isArray(blinkStepIdx) || blinkStepIdx.length === 0) return;
    const firstIdx = blinkStepIdx[0];
    // Wait a frame so the freshly-added DOM row is mounted and registered in rowRefs.
    const raf = requestAnimationFrame(() => {
      const el = rowRefs.current.get(firstIdx);
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
    return () => cancelAnimationFrame(raf);
  }, [blinkStepIdx]);

  // Close menu on outside click
  useEffect(() => {
    const handleOutside = (e: Event) => {
      if (openMenuIdx === null) return;
      const btnEl = menuBtnRefs.current.get(openMenuIdx);
      const portalEl = portalMenuRef.current;
      const target = e.target as Node;
      if ((!btnEl || !btnEl.contains(target)) && (!portalEl || !portalEl.contains(target))) {
        setOpenMenuIdx(null);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [openMenuIdx]);

  useEffect(() => {
    if (isLoading) {
      shimmerStartedAtRef.current = Date.now();
      setShowStepsShimmer(true);
      setInsertZonesArmed(false);
      setInsertZonesReady(false);
      return;
    }

    if (!showStepsShimmer) return;

    const elapsed = Date.now() - shimmerStartedAtRef.current;
    const delay = Math.max(0, STEPS_SHIMMER_CYCLE_MS - elapsed);
    const timer = window.setTimeout(() => setShowStepsShimmer(false), delay);
    return () => window.clearTimeout(timer);
  }, [isLoading, showStepsShimmer]);

  useEffect(() => {
    if (showStepsShimmer || testCaseSteps.length === 0) {
      setInsertZonesArmed(false);
      setInsertZonesReady(false);
      return;
    }

    setInsertZonesArmed(false);
    setInsertZonesReady(false);
    const timer = window.setTimeout(() => setInsertZonesArmed(true), STEPS_ROWS_SETTLE_MS);
    return () => window.clearTimeout(timer);
  }, [showStepsShimmer, stepRowsSignature, testCaseSteps.length]);

  const resolveSubsystem = (step: TestCaseStep): string => {
    const label = step.subsystem?.label ?? "";
    if (label && label !== "All") return label;

    const normalizeText = (value: string | undefined): string => (value ?? "").trim().toLowerCase();
    const parseCommandNameFromLabel = (value: string | undefined): string => {
      const raw = value ?? "";
      const parts = raw.split(" - ");
      return parts.length > 1 ? parts.slice(1).join(" - ") : raw;
    };

    const stepCommand = step.teleData as { label: string; value: unknown } | null;
    const stepCommandValue = stepCommand?.value as Record<string, unknown> | undefined;
    const stepSequenceData = stepCommandValue?.sequenceData as Record<string, unknown> | undefined;
    const stepCommandId = stepSequenceData?.commandId as number | undefined;
    const stepCommandName = normalizeText((stepCommandValue?.name as string | undefined) ?? parseCommandNameFromLabel(stepCommand?.label));
    const stepCommandLabel = normalizeText(stepCommand?.label);

    for (const comp of busComponents) {
      if (comp.label === "All") continue;
      const cmds = comp.value as { label: string; value: unknown }[];
      if (!Array.isArray(cmds)) continue;

      const matched = cmds.some((cmd) => {
        const cmdValue = cmd.value as Record<string, unknown> | undefined;
        const cmdSequenceData = cmdValue?.sequenceData as Record<string, unknown> | undefined;
        const cmdId = cmdSequenceData?.commandId as number | undefined;
        const cmdName = normalizeText((cmdValue?.name as string | undefined) ?? parseCommandNameFromLabel(cmd.label));
        const cmdLabel = normalizeText(cmd.label);

        if (stepCommandId != null && cmdId != null) {
          if (stepCommandId !== cmdId) return false;
          if (stepCommandName && cmdName) return stepCommandName === cmdName;
          return true;
        }
        if (stepCommandName && cmdName) return stepCommandName === cmdName;
        return !!stepCommandLabel && stepCommandLabel === cmdLabel;
      });

      if (matched) return comp.label;
    }
    return "";
  };

  const openMenu = (e: MouseEvent<HTMLButtonElement>, idx: number): void => {
    e.stopPropagation();
    if (openMenuIdx === idx) { setOpenMenuIdx(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    setMenuPos({ top: rect.bottom + 4, left: rect.right - 130 });
    setOpenMenuIdx(idx);
  };

  const handleEditAction = (idx: number) => {
    setOpenMenuIdx(null);
    onEditStep(idx);
  };

  const totalCount = testCaseSteps.length + (selectedCommand ? 1 : 0);

  useEffect(() => {
    if (showStepsDialog) {
      setRecalcTrigger(prev => !prev);
    }
  }, [showStepsDialog])

  return (
    <div className="tcd-steps-overlay" onClick={() => { setSelectedDetailIdx(null); onClose(); }}>
      <div className="tcd-steps-dialog" onClick={(e) => e.stopPropagation()} data-testid="steps-dialog-container">

        {/* Header */}
        <div className="tcd-steps-dialog__header" data-testid="steps-dialog-header">
          <div className="tcd-steps-dialog__title-group">
              <InlineInput
                placeholder="Enter Test Case Title"
                value={testCaseName || ""}
                onChange={(value) => onTestCaseNameChange(value)}
                onBlur={() => {
                  const trimmed = (testCaseName || "").trim();
                  if (trimmed) {
                    onTestCaseNameChange(trimmed);
                  }
                  setIsEditingName(false);
                }}
                showTooltip={true}
                tooltipTitle={testCaseName || ""}
                tooltipPlacement='top-start'
                triggerRecalc={recalcTrigger}
                data-testid="steps-dialog-title-input"
              />
            <div className="tcd-steps-dialog__tc-badge" data-testid="steps-dialog-badge">
              TC <span className="tcd-steps-dialog__tc-count">{totalCount}</span>
            </div>
            {/* Copy/Paste/Clear cluster — always rendered when there are steps; buttons enable based on state */}
            {!isLoading && testCaseSteps.length > 0 && (
              <div className="tcd-steps-dialog__select-actions">
                {selectedIds.size > 0 && (
                  <span className="tcd-steps-dialog__select-count">
                    {selectedIds.size} selected{clipboard ? ` · ${clipboard.length} copied` : ""}
                  </span>
                )}
                <CustomToolTip
                  title={selectedIds.size > 0 ? `Copy ${selectedIds.size} ${selectedIds.size === 1 ? "item" : "items"}` : "Copy (select rows first)"}
                  placement="top"
                >
                  <button
                    type="button"
                    className="tcd-steps-dialog__select-btn"
                    onClick={handleCopy}
                    disabled={selectedIds.size === 0}
                    aria-label="Copy selection"
                    data-testid="steps-dialog-copy-btn"
                  >
                    <CopyIcon />
                  </button>
                </CustomToolTip>
                <CustomToolTip
                  title={clipboard ? `Paste ${clipboard.length} ${clipboard.length === 1 ? "item" : "items"} at end` : "Paste (copy items first)"}
                  placement="top"
                >
                  <button
                    type="button"
                    className="tcd-steps-dialog__select-btn"
                    onClick={handlePaste}
                    disabled={!clipboard || !onPasteSteps}
                    aria-label="Paste at end"
                    data-testid="steps-dialog-paste-btn"
                  >
                    <PasteIcon />
                  </button>
                </CustomToolTip>
              </div>
            )}
          </div>
          <button className="tcd-steps-dialog__close" type="button" onClick={() => { setSelectedDetailIdx(null); onClose(); }} data-testid="steps-dialog-close-btn">
            <CloseFadedIcon />
          </button>
        </div>

        {/* Body */}
        <div className="tcd-steps-dialog__body">
          <div
            className={`tcd-steps-table${selectedDetailIdx !== null ? " tcd-steps-table--detail-open" : ""}`}
            onPointerMove={(e) => {
              if (insertZonesArmed && !insertZonesReady && (e.movementX !== 0 || e.movementY !== 0)) {
                setInsertZonesReady(true);
              }
            }}
          >
            {!showStepsShimmer && testCaseSteps.length === 0 && !selectedCommand ? (
              <div className="tcd-steps-empty-state">
                <div className="tcd-steps-empty-state__icon"><EmptyPageIcon /></div>
                <div className="tcd-steps-empty-state__text">
                  <span className="tcd-steps-empty-state__title">No Preview</span>
                  <span className="tcd-steps-empty-state__subtitle">Add Telecommand to edit Validation</span>
                </div>
                <button className="tcd-steps-empty-state__btn" type="button" onClick={() => onAddTelecommand()}>
                  <span className="tcd-steps-empty-state__btn-icon"><AddTelecommandIcon /></span>
                  <span className="tcd-steps-empty-state__btn-text">Add Telecommand</span>
                </button>
              </div>
            ) : (
              <>
                <div className="tcd-steps-table__head">
                  <div className="tcd-steps-table__hcell tcd-steps-table__hcell--drag"></div>
                  <div className="tcd-steps-table__hcell tcd-steps-table__hcell--check">
                    {!isLoading && testCaseSteps.length > 0 && (
                      <button
                        type="button"
                        className="tc-checkbox-btn"
                        onClick={toggleSelectAll}
                        aria-label="Select all"
                        role="checkbox"
                        aria-checked={isAllSelected ? true : isPartiallySelected ? "mixed" : false}
                        data-testid="steps-dialog-select-all-checkbox"
                      >
                        {isAllSelected ? <CheckboxCheckedIcon /> : isPartiallySelected ? <CheckboxIndeterminateIcon /> : <CheckboxUncheckedIcon />}
                      </button>
                    )}
                  </div>
                  <div className="tcd-steps-table__hcell tcd-steps-table__hcell--sno">#</div>
                  <div className="tcd-steps-table__hcell tcd-steps-table__hcell--icon"></div>
                  <div className="tcd-steps-table__hcell tcd-steps-table__hcell--tc">TELECOMMAND</div>
                  <div className="tcd-steps-table__hcell tcd-steps-table__hcell--subsystem">SUBSYSTEM</div>
                  <div className="tcd-steps-table__hcell tcd-steps-table__hcell--delay">DELAY</div>
                  <div className="tcd-steps-table__hcell tcd-steps-table__hcell--delay">TIMEOUT</div>
                  <div className="tcd-steps-table__hcell tcd-steps-table__hcell--action" />
                </div>
                <div className="tcd-steps-table__body">
                  {showStepsShimmer ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <div key={i} className="tcd-steps-table__row tcd-steps-shimmer-row">
                        <div className="tcd-steps-table__cell tcd-steps-table__cell--drag">
                          <div className="tcd-steps-shimmer tcd-steps-shimmer--num" />
                        </div>
                        <div className="tcd-steps-table__cell tcd-steps-table__cell--check" />
                        <div className="tcd-steps-table__cell tcd-steps-table__cell--sno">
                          <div className="tcd-steps-shimmer tcd-steps-shimmer--num" />
                        </div>
                        <div className="tcd-steps-table__cell tcd-steps-table__cell--icon">
                          <div className="tcd-steps-shimmer tcd-steps-shimmer--icon" />
                        </div>
                        <div className="tcd-steps-table__cell tcd-steps-table__cell--tc">
                          <div
                            className="tcd-steps-shimmer tcd-steps-shimmer--tc"
                            style={{ '--tcd-shimmer-w': `${55 + (i % 3) * 15}%` } as CSSProperties}
                          />
                        </div>
                        <div className="tcd-steps-table__cell tcd-steps-table__cell--subsystem">
                          <div className="tcd-steps-shimmer tcd-steps-shimmer--tag" />
                        </div>
                        <div className="tcd-steps-table__cell tcd-steps-table__cell--delay">
                          <div className="tcd-steps-shimmer tcd-steps-shimmer--delay" />
                        </div>
                        <div className="tcd-steps-table__cell tcd-steps-table__cell--delay">
                          <div className="tcd-steps-shimmer tcd-steps-shimmer--delay" />
                        </div>
                        <div className="tcd-steps-table__cell tcd-steps-table__cell--action" />
                      </div>
                    ))
                  ) : (
                    testCaseSteps.map((step, idx) => {
                      const canReorder = testCaseSteps.length > 1;
                      const isActive = selectedDetailIdx === idx;
                      const subsystemLabel = resolveSubsystem(step);
                      const isDragOver = dragOverIdx === idx && draggingIdx !== idx;
                      const isDragging = draggingIdx === idx;
                      const rowId = stepIds[idx];
                      const isSelected = selectedIds.has(rowId);
                      return (
                        <div
                          key={rowId}
                          ref={el => { if (el) rowRefs.current.set(idx, el); else rowRefs.current.delete(idx); }}
                          className={`tcd-steps-table__row${isActive ? " active" : ""}${isDragOver ? " drag-over" : ""}${isDragging ? " dragging" : ""}${isBlinking(idx) ? " tcd-step-row--blink" : ""}${canReorder ? " tcd-steps-table__row--reorderable" : ""}${isSelected ? " tcd-steps-table__row--selected" : ""}`}
                          draggable={canReorder}
                          onDragStart={() => canReorder && setDraggingIdx(idx)}
                          onDragOver={(e) => { e.preventDefault(); if (canReorder) setDragOverIdx(idx); }}
                          onDrop={(e) => {
                            e.preventDefault();
                            if (canReorder && draggingIdx !== null && draggingIdx !== idx) {
                              onReorderStep(draggingIdx, idx);
                            }
                            setDraggingIdx(null);
                            setDragOverIdx(null);
                          }}
                          onDragEnd={() => { setDraggingIdx(null); setDragOverIdx(null); }}
                          onClick={() => {
                            setSelectedDetailIdx(idx);
                            rowRefs.current.get(idx)?.scrollIntoView({ behavior: "smooth", block: "center" });
                          }}
                          // Open edit view directly on row double-click
                          onDoubleClick={() => handleEditAction(idx)}
                          data-testid={`steps-dialog-row-${idx}`}
                        >
                          {/* ── Insert Zone (appears on hover) ── */}
                          {insertZonesReady && (
                            <div className="tcd-steps-insert-zone">
                              <CustomToolTip title="Insert Telecommand" placement="top">
                                <button
                                  type="button"
                                  className="tcd-steps-insert-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onAddTelecommand(idx + 1);
                                  }}
                                  data-testid={`steps-dialog-insert-btn-${idx}`}
                                >
                                  <PlusIcon />
                                </button>
                              </CustomToolTip>
                            </div>
                          )}

                          <div className="tcd-steps-table__cell tcd-steps-table__cell--drag">
                            <span className={`tcd-steps-drag-handle${!canReorder ? " tcd-steps-drag-handle--disabled" : ""}`}>
                              <DragHandleIcon />
                            </span>
                          </div>
                          <div
                            className="tcd-steps-table__cell tcd-steps-table__cell--check"
                            onClick={e => e.stopPropagation()}
                            onMouseDown={e => e.stopPropagation()}
                            onDoubleClick={e => e.stopPropagation()}
                            draggable={false}
                          >
                            <button
                              type="button"
                              className="tc-checkbox-btn"
                              onClick={e => { e.stopPropagation(); toggleRowSelection(rowId, e); }}
                              onDoubleClick={e => e.stopPropagation()}
                              onMouseDown={e => e.stopPropagation()}
                              aria-label={`Select step ${idx + 1}`}
                              aria-checked={isSelected}
                              role="checkbox"
                              data-testid={`steps-dialog-checkbox-${idx}`}
                            >
                              {isSelected ? <CheckboxCheckedIcon /> : <CheckboxUncheckedIcon />}
                            </button>
                          </div>
                          <div className="tcd-steps-table__cell tcd-steps-table__cell--sno">
                            {idx + 1}
                          </div>
                          <div className="tcd-steps-table__cell tcd-steps-table__cell--icon">
                            <HexIcon />
                          </div>
                          <div className="tcd-steps-table__cell tcd-steps-table__cell--tc" title={step.teleData?.label ?? ""}>
                            <span data-testid={`steps-dialog-tc-label-${idx}`}>{step.teleData?.label ?? "—"}</span>
                            {step.isCritical && (
                              <CustomToolTip title="Critical Command" placement="top">
                                <span className="tcd-critical-icon-cell">
                                  <CriticalIcon />
                                </span>
                              </CustomToolTip>
                            )}
                          </div>
                          <div className="tcd-steps-table__cell tcd-steps-table__cell--subsystem">
                            {subsystemLabel && (
                              <span className="tcd-steps-tag tcd-steps-tag--filled">
                                <SubsysIcon label={subsystemLabel} />{subsystemLabel}
                              </span>
                            )}
                          </div>
                          <div className="tcd-steps-table__cell tcd-steps-table__cell--delay">{formatMs(step.delay)}</div>
                          <div className="tcd-steps-table__cell tcd-steps-table__cell--delay">{formatMs(step.timeout)}</div>
                          <div className="tcd-steps-table__cell tcd-steps-table__cell--action" onClick={e => e.stopPropagation()}>
                            <button
                              ref={el => { if (el) menuBtnRefs.current.set(idx, el); else menuBtnRefs.current.delete(idx); }}
                              className={`tcd-steps-menu-btn${openMenuIdx === idx ? " active" : ""}`}
                              type="button"
                              onClick={(e) => openMenu(e, idx)}
                              data-testid={`steps-dialog-menu-btn-${idx}`}
                            >
                              <ThreeDotsIcon />
                            </button>
                          </div>
                        </div>
                      );
                    })
                  )}

                  {/* Current in-progress telecommand row */}
                  {selectedCommand && (
                    <div className="tcd-steps-table__row tcd-steps-table__row--current" data-testid="steps-dialog-row-current">
                      <div className="tcd-steps-table__cell tcd-steps-table__cell--drag"></div>
                      <div className="tcd-steps-table__cell tcd-steps-table__cell--check" />
                      <div className="tcd-steps-table__cell tcd-steps-table__cell--sno">
                        <span className="tcd-steps-row-number">{testCaseSteps.length + 1}</span>
                      </div>
                      <div className="tcd-steps-table__cell tcd-steps-table__cell--icon">
                        <HexIcon />
                      </div>
                      <div className="tcd-steps-table__cell tcd-steps-table__cell--tc" title={selectedCommand.label}>
                        {selectedCommand.label}
                      </div>
                      <div className="tcd-steps-table__cell tcd-steps-table__cell--subsystem">
                        {component?.label && component.label !== "All" && (
                          <span className="tcd-steps-tag tcd-steps-tag--outlined" title={component.label}>
                            <SubsysIcon label={component.label} />
                          </span>
                        )}
                      </div>
                      <div className="tcd-steps-table__cell tcd-steps-table__cell--delay">{formatMs(delay)}</div>
                      <div className="tcd-steps-table__cell tcd-steps-table__cell--delay">{formatMs(timeout)}</div>
                      <div className="tcd-steps-table__cell tcd-steps-table__cell--action" />
                    </div>
                  )}
                </div>
                {(testCaseSteps.length > 0 || !!selectedCommand) && (
                  <button className="tcd-steps-add-btn" type="button" onClick={() => onAddTelecommand()} disabled={showStepsShimmer} data-testid="steps-dialog-add-tc-btn">
                    <AddTelecommandIcon />
                    Add Telecommand
                  </button>
                )}
              </>
            )}
          </div>

          {/* Detail overlay */}
          {selectedDetailIdx !== null && testCaseSteps[selectedDetailIdx] && (
            <StepDetailOverlay
              step={testCaseSteps[selectedDetailIdx]}
              onClose={() => setSelectedDetailIdx(null)}
            />
          )}
        </div>
      </div>

      {/* Three-dot context menu portal */}
      {openMenuIdx !== null && ReactDOM.createPortal(
        <div
          className="tc-context-menu"
          ref={portalMenuRef}
          style={{ '--tc-menu-top': `${menuPos.top}px`, '--tc-menu-left': `${menuPos.left}px` } as CSSProperties}
          onClick={e => e.stopPropagation()}
        >
          <div className="tc-context-menu__inner">
            <button className="tc-context-menu__item" type="button" onClick={() => { if (openMenuIdx !== null) handleEditAction(openMenuIdx); }} data-testid="steps-dialog-menu-edit-btn">
              Edit
            </button>
            <button className="tc-context-menu__item" type="button" onClick={() => { const idx = openMenuIdx; setOpenMenuIdx(null); onCloneStep(idx); }} data-testid="steps-dialog-menu-clone-btn">
              Clone
            </button>
            <div className="tc-context-menu__divider" />
            <button
              className="tc-context-menu__item tc-context-menu__item--delete"
              type="button"
              onClick={() => { const idx = openMenuIdx; setOpenMenuIdx(null); onDeleteStep(idx); }}
              data-testid="steps-dialog-menu-delete-btn"
            >
              Delete
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default StepsDialog;
